/**
 * Day handling, in the user's local calendar.
 *
 * Everything here works on `YYYY-MM-DD` strings rather than Date objects on
 * purpose. A streak is a question about the calendar, not about elapsed time:
 * a set finished at 23:50 and one at 00:10 are different days even though they
 * are twenty minutes apart, and a set at 02:00 is not "yesterday plus a bit".
 * Storing the local date up front also means a phone crossing a timezone does
 * not retroactively rewrite which days she practised.
 */

export type IsoDate = string;

export function toIsoDate(date: Date): IsoDate {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function today(now: Date = new Date()): IsoDate {
  return toIsoDate(now);
}

export function parseIsoDate(iso: IsoDate): Date {
  const [y, m, d] = iso.split('-').map(Number);
  // Local midnight, not UTC — `new Date('2026-08-11')` parses as UTC and
  // lands on the previous day for anyone west of Greenwich.
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const date = parseIsoDate(iso);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
}

/** Whole days from `from` to `to`. Negative when `to` is earlier. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  // Compare at local midday so a daylight-saving shift cannot round a
  // 23-hour or 25-hour day to the wrong whole number.
  a.setHours(12, 0, 0, 0);
  b.setHours(12, 0, 0, 0);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

export function isValidIsoDate(value: unknown): value is IsoDate {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}
